import { useState } from "react";
import { Link } from "react-router";

import { Empty, Failure, Panel } from "../components/Panel.js";
import { useResource } from "../hooks.js";
import { apiPost } from "../lib/api.js";
import { type Organization } from "../types.js";

const PATH = "/app/control/organization";

export function OrganizationsPage() {
  const organizations = useResource<Organization[]>(PATH);
  const [alias, setAlias] = useState("");
  const [name, setName] = useState("");
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);

  return (
    <>
      <Panel title="Create an organization">
        <form
          className="flex flex-wrap gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!alias.trim() || !name.trim()) return;
            setBusy(true);
            setFailure(undefined);
            try {
              await apiPost(PATH, { alias: alias.trim(), name: name.trim() });
              setAlias("");
              setName("");
              organizations.reload();
            } catch (error) {
              setFailure((error as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Acme Corporation"
            className="flex-1 rounded border border-black/15 px-3 py-2"
          />
          <input
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            placeholder="acme"
            className="w-40 rounded border border-black/15 px-3 py-2 font-mono"
          />
          <button
            type="submit"
            disabled={busy || !alias.trim() || !name.trim()}
            className="rounded bg-jay px-4 py-2 font-medium text-white hover:opacity-90 disabled:opacity-40"
          >
            Create
          </button>
        </form>
        {failure ? (
          <div className="mt-4">
            <Failure>{failure}</Failure>
          </div>
        ) : null}
      </Panel>

      <Panel title="Organizations">
        {organizations.loading ? <Empty>Loading</Empty> : null}
        {organizations.error ? (
          <Failure>{organizations.error.message}</Failure>
        ) : null}
        {organizations.value && organizations.value.length === 0 ? (
          <Empty>No organizations yet</Empty>
        ) : null}
        {organizations.value && organizations.value.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="text-ink/50">
              <tr>
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Alias</th>
                <th className="pb-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {organizations.value.map((organization) => (
                <tr key={organization.id} className="border-t border-black/5">
                  <td className="py-2">{organization.name}</td>
                  <td className="py-2 font-mono">{organization.alias}</td>
                  <td className="py-2">{organization.status}</td>
                  <td className="py-2 text-right">
                    <Link
                      to={`/members?organization=${organization.id}`}
                      className="mr-3 text-jay hover:underline"
                    >
                      Members
                    </Link>
                    <Link
                      to={`/keys?organization=${organization.id}`}
                      className="text-jay hover:underline"
                    >
                      Keys
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Panel>
    </>
  );
}
